import React, { FC, ErrorInfo, useCallback, useEffect } from 'react'
import { SimulationResult } from '../api/selfApi'
import {
  colorMap,
  makeProfitChart,
  makeSampleChart,
  makeSpChart,
  resetChart,
} from '../js/iframeSimulationChart'
import { useDispatch } from 'react-redux'
import { selectCase } from '../store/modules/simulation'
import '../assets/css/libs.css'
import '../assets/css/style.css'

export interface ErrorBoundaryProps {
  notifyError?: (error: Error, info: ErrorInfo) => void
}

export interface SimulationProps {
  result: SimulationResult | undefined
}

export interface SimulationState {
  selectedCase: string | undefined
}
interface ChartSeries {
  type: string
  name?: string
  color?: string
  borderRadiusTopLeft?: number | string
  borderRadiusTopRight?: number | string
  data: ({
    y?: number
    color?: string
    actValue?: number
    sample?: number
  } | null)[]
}

function makeProfitChartData(profit: number | undefined, principal: number): number | undefined {
  return profit !== undefined ? (profit > 0 ? principal : principal - Math.abs(profit)) : undefined
}

function makeProfitChartSeries(result: SimulationResult): ChartSeries[] | null {
  // TODO profit < 0 => principal - profit
  return result
    ? [
        {
          type: 'column',
          name: '評価益',
          borderRadiusTopLeft: '10%',
          borderRadiusTopRight: '10%',
          data: [
            null,
            {
              y: result.depositAmount - result.principal,
              color: '#67a89b',
              actValue: result.depositAmount - result.principal,
            },
            {
              y: result.veryBad.profit ? Math.abs(result.veryBad.profit) : undefined,
              color:
                result.veryBad.profit === undefined || result.veryBad.profit > 0
                  ? colorMap.plus.normal.veryBad
                  : colorMap.minus.normal.veryBad,
              actValue: result.veryBad.profit,
              sample: result.veryBad.sample,
            },
            {
              y: result.bad.profit ? Math.abs(result.bad.profit) : undefined,
              color:
                result.bad.profit === undefined || result.bad.profit > 0
                  ? colorMap.plus.normal.bad
                  : colorMap.minus.normal.bad,
              actValue: result.bad.profit,
              sample: result.bad.sample,
            },
            {
              y: result.littleBad.profit ? Math.abs(result.littleBad.profit) : undefined,
              color:
                result.littleBad.profit === undefined || result.littleBad.profit > 0
                  ? colorMap.plus.normal.littleBad
                  : colorMap.minus.normal.littleBad,
              actValue: result.littleBad.profit,
              sample: result.littleBad.sample,
            },
            {
              y: result.median.profit ? Math.abs(result.median.profit) : undefined,
              color:
                result.median.profit === undefined || result.median.profit > 0
                  ? colorMap.plus.normal.median
                  : colorMap.minus.normal.median,
              actValue: result.median.profit,
              sample: result.median.sample,
            },
            {
              y: result.littleGood.profit ? Math.abs(result.littleGood.profit) : undefined,
              color:
                result.littleGood.profit === undefined || result.littleGood.profit > 0
                  ? colorMap.plus.normal.littleGood
                  : colorMap.minus.normal.littleGood,
              actValue: result.littleGood.profit,
              sample: result.littleGood.sample,
            },
            {
              y: result.good.profit ? Math.abs(result.good.profit) : undefined,
              color:
                result.good.profit === undefined || result.good.profit > 0
                  ? colorMap.plus.normal.good
                  : colorMap.minus.normal.good,
              actValue: result.good.profit,
              sample: result.good.sample,
            },
            {
              y: result.veryGood.profit ? Math.abs(result.veryGood.profit) : undefined,
              color:
                result.veryGood.profit === undefined || result.veryGood.profit > 0
                  ? colorMap.plus.normal.veryGood
                  : colorMap.minus.normal.veryGood,
              actValue: result.veryGood.profit,
              sample: result.veryGood.sample,
            },
          ],
        },
        {
          type: 'column',
          name: '投資元本',
          color: colorMap.principal.normal,
          data: [
            {
              y: result.principal,
            },
            {
              y: result.principal,
            },
            {
              y: makeProfitChartData(result.veryBad.profit, result.principal),
            },
            {
              y: makeProfitChartData(result.bad.profit, result.principal),
            },
            {
              y: makeProfitChartData(result.littleBad.profit, result.principal),
            },
            {
              y: makeProfitChartData(result.median.profit, result.principal),
            },
            {
              y: makeProfitChartData(result.littleGood.profit, result.principal),
            },
            {
              y: makeProfitChartData(result.good.profit, result.principal),
            },
            {
              y: makeProfitChartData(result.veryGood.profit, result.principal),
            },
          ],
        },
      ]
    : null
}

function makeSampleChartSeries(result: SimulationResult): ChartSeries[] | null {
  return result
    ? [
        {
          type: 'column',
          borderRadiusTopLeft: '20%',
          borderRadiusTopRight: '20%',
          data: [
            {},
            {},
            { y: result.veryBad.sample },
            { y: result.bad.sample },
            { y: result.littleBad.sample },
            { y: result.median.sample },
            { y: result.littleGood.sample },
            { y: result.good.sample },
            { y: result.veryGood.sample },
          ],
        },
      ]
    : null
}

export const IframeSimulationChart: FC<SimulationProps> = React.memo(({ result }) => {
  const dispatch = useDispatch()
  const selectedCallBack = useCallback(
    (idx: number) => {
      dispatch(selectCase(idx))
    },
    [dispatch]
  )

  useEffect(() => {
    if (result) {
      makeProfitChart('profitChart', makeProfitChartSeries(result), selectedCallBack)
      makeSampleChart('sampleChart', makeSampleChartSeries(result), selectedCallBack)
      makeSpChart('simulationSpChart', makeProfitChartSeries(result), selectedCallBack)
    } else {
      resetChart()
    }
  })
  return (
    <div className="u-mt30px">
      <div id="profitChart" className="chart__pc-only" />
      <div id="sampleChart" className="chart__pc-only" />
      <div id="simulationSpChart" className="chart__sp-only" />
      <p className="c-note">
        上記のシミュレーション結果は、入力された「リターン」および「リスク」の値を用いて、モンテカルロ法を用いて計算しております。
      </p>
    </div>
  )
})
